import { useInfiniteQuery } from '@tanstack/react-query';

import { individualsService, FetchIndividualsRequest, useToast } from '@aslanpay/portal-common-lib';

import { EmployeesPage } from 'app/components/UI/Table/Table.types';

import { isSameDayButBeforeUTCHour, validateFutureDate } from 'app/utils/validations';

type UseInfiniteFetchEmployeesProps = Omit<FetchIndividualsRequest, 'page'> & {
  size: number;
  cacheBuster?: number;
  enabled?: boolean;
};

// ⚠️ Careful using this hook outside of the gifting flow, it defaults to fetching only giftable employees
// TODO - this component should not be tightly coupled with the gifting flow
const useInfiniteFetchEmployees = ({
  ids,
  status,
  role,
  companyId,
  friendlyId,
  payCalendarId,
  externalPayrollId,
  email,
  isTerminationDateSet,
  onUnpaidLeave,
  allowTracking,
  allowMarketing,
  query,
  sortBy,
  sort,
  size,
  cacheBuster,
  enabled = true,
}: UseInfiniteFetchEmployeesProps) => {
  const toast = useToast();

  return useInfiniteQuery<EmployeesPage, Error>({
    queryKey: [
      'infiniteFetchEmployees',
      ids,
      status,
      role,
      companyId,
      friendlyId,
      payCalendarId,
      externalPayrollId,
      email,
      isTerminationDateSet,
      onUnpaidLeave,
      allowTracking,
      allowMarketing,
      query,
      sortBy,
      sort,
      size,
      cacheBuster, // Include cache buster in query key
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const employees = await individualsService.fetchIndividuals({
        ids,
        status,
        role,
        companyId,
        friendlyId,
        payCalendarId,
        externalPayrollId,
        email,
        isTerminationDateSet,
        onUnpaidLeave,
        allowTracking,
        allowMarketing,
        query,
        sortBy,
        sort,
        page: pageParam,
        size,
      });

      // Process employee status without additional API calls
      const employeesList = employees.list.map((employee) => {
        const employeeStatus =
          employee.terminationDate &&
          (validateFutureDate(employee.terminationDate) ||
            isSameDayButBeforeUTCHour(employee.terminationDate, 18))
            ? 'LEAVING'
            : employee.status;

        return { ...employee, companyName: '', status: employeeStatus };
      });

      return { ...employees, list: employeesList };
    },
    getNextPageParam: (lastPage) => {
      const hasMore = (lastPage.pageNumber + 1) * lastPage.pageSize < lastPage.total;
      return hasMore ? lastPage.pageNumber + 1 : undefined;
    },
    enabled,
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: `Sorry, we failed to fetch the employees${
          companyId?.length ? ' for this company' : ''
        }. Please try again.`,
      });
    },
  });
};

export default useInfiniteFetchEmployees;
